const getCapacitorModules = () => {
  try {
    // Keep runtime loading so web/Jest environments can fall back cleanly.
    // eslint-disable-next-line global-require
    const { Capacitor } = require('@capacitor/core');
    // eslint-disable-next-line global-require
    const { Camera, CameraResultType, CameraSource } = require('@capacitor/camera');
    return { Capacitor, Camera, CameraResultType, CameraSource };
  } catch (error) {
    return null;
  }
};

const extensionFromFormat = (format) => {
  const normalized = String(format || '').toLowerCase();
  if (normalized === 'jpeg' || normalized === 'jpg') {
    return 'jpg';
  }
  if (normalized === 'png' || normalized === 'webp' || normalized === 'heic') {
    return normalized;
  }
  return 'jpg';
};

const mimeFromFormat = (format) => {
  const ext = extensionFromFormat(format);
  if (ext === 'jpg') {
    return 'image/jpeg';
  }
  return `image/${ext}`;
};

const fileFromBlob = (blob, fileNamePrefix, formatHint) => {
  const extension = extensionFromFormat(formatHint);
  const mimeType = blob.type || mimeFromFormat(formatHint);
  return new File(
    [blob],
    `${fileNamePrefix}-${Date.now()}.${extension}`,
    { type: mimeType, lastModified: Date.now() }
  );
};

const fileFromPhoto = async (photo, fileNamePrefix) => {
  if (photo?.webPath) {
    const response = await fetch(photo.webPath);
    if (!response.ok) {
      throw new Error('Failed to load native photo URI');
    }
    const blob = await response.blob();
    return fileFromBlob(blob, fileNamePrefix, photo.format);
  }

  if (photo?.base64String) {
    const mimeType = mimeFromFormat(photo.format);
    const response = await fetch(`data:${mimeType};base64,${photo.base64String}`);
    const blob = await response.blob();
    return fileFromBlob(blob, fileNamePrefix, photo.format);
  }

  if (photo?.dataUrl) {
    const response = await fetch(photo.dataUrl);
    const blob = await response.blob();
    return fileFromBlob(blob, fileNamePrefix, photo.format);
  }

  throw new Error('Native photo payload did not include image data');
};

export const isNativeMobilePlatform = () => {
  const modules = getCapacitorModules();
  if (!modules) {
    return false;
  }

  const { Capacitor } = modules;
  return Capacitor.isNativePlatform() && ['android', 'ios'].includes(Capacitor.getPlatform());
};

export const captureWithNativeCamera = async () => {
  const modules = getCapacitorModules();
  if (!modules || !isNativeMobilePlatform()) {
    return null;
  }

  const { Camera, CameraResultType, CameraSource } = modules;
  const photo = await Camera.getPhoto({
    source: CameraSource.Camera,
    resultType: CameraResultType.Uri,
    quality: 92,
    allowEditing: false,
    correctOrientation: true,
  });
  return fileFromPhoto(photo, 'receipt-camera');
};

export const pickFromNativeGallery = async () => {
  const modules = getCapacitorModules();
  if (!modules || !isNativeMobilePlatform()) {
    return null;
  }

  const { Camera, CameraResultType, CameraSource } = modules;
  const photo = await Camera.getPhoto({
    source: CameraSource.Photos,
    resultType: CameraResultType.Uri,
    quality: 92,
    allowEditing: false,
    correctOrientation: true,
  });
  return fileFromPhoto(photo, 'receipt-gallery');
};
